/**
 * Shared quantity-weighted source-attribution log for `checkout_section`.
 *
 * Sibling of assets/wonderful-experience-source-log.js (`_experience_source_log`
 * / `checkout_experience`) — same architecture, independent attribute, purely
 * additive. Do not merge the two logs or their attributes; they answer
 * different questions (classic-vs-chat experience vs. which on-page section
 * the add came from) and are read independently downstream.
 *
 * Tracks which SECTION added each unit currently in the cart, via the
 * `_section_source_log` cart attribute — a JSON object keyed by variant id,
 * each value an array with one `section_id` entry per unit of that variant,
 * in add order (LIFO on removal, same as `_experience_source_log`).
 *
 * `section_id` values are the EXACT same ones already used for the
 * `section_viewed`/`section_engaged`/`product_added` GA4 events (see
 * assets/wonderful-ga4-tracking.js and the `data-section-id` attributes in
 * sections/featured-collections.liquid and wonderful-cart/App.tsx):
 *   - featured_collection_grid_<page> (on product templates, and on static Pages dedicated to one
 *     product-group type e.g. /pages/varnish-cups, <page> is the same shared "type_<category>"
 *     value as product_page_ below, not the raw product/page handle. Collection templates are
 *     unaffected, even for a collection named after a product-group type — see
 *     sections/featured-collections.liquid)
 *   - new_product_grid_<page> (same "type_<category>" treatment as featured_collection_grid_ above,
 *     for the same two cases — see wonderful-cart/App.tsx's newProductGridPageContext.
 *     chat_experience_ is untouched and still uses the raw per-page context.)
 *   - chat_experience_<page>
 *   - product_page_type_<category> (type_varnish_sticks / type_varnish_cups / type_prophy_paste /
 *     type_adult_prophy_paste / type_accessories / type_other — derived from the
 *     custom.product_sub_group metafield, not product.handle; the "type_" marker distinguishes
 *     this metafield-derived category from an unrelated raw handle that might read the same —
 *     see snippets/product-page-section-group.liquid)
 * Any of the above may additionally carry a "CR_" prefix (e.g. CR_featured_collection_grid_index)
 * when window.__WONDERFUL_CLASSIC_REQUESTED__ is set — see recordDelta() below and the click handler
 * in sections/wonderful-cart-order-hero.liquid. Page-lifetime-only, never persisted.
 * A mutation whose section isn't known (cart drawer's own line-item controls,
 * main-collection-product-grid, quiz recommendations, native /cart page,
 * third-party apps, etc.) simply isn't logged here — same "untracked" concept
 * as `_experience_source_log` — so it doesn't vote in `checkout_section` but
 * still counts toward a variant's tracked array length via reconciliation.
 *
 * Consumed by:
 *  - assets/checkout-experience.js (classic checkout entry point)
 *  - assets/cart-drawer-ie.js (featured-collections grid mutations)
 *  - assets/product-form.js (product-page hero add-to-cart)
 *  - wonderful-cart/utils/sessionCartSync.ts (chat checkout + sync entry points)
 *  - wonderful-cart/hooks/useShopifyCart.ts (chat/grid mutations)
 *
 * Optionally uses assets/wonderful-cart-log-shared.js (should load first, but falls back to a local
 * implementation if it didn't) for the cart-URL/fetch/live-quantity helpers shared with the sibling
 * wonderful-experience-source-log.js. Must itself load before all of the consumers above; either
 * sibling log can load before the other.
 */
;(function () {
  var ATTR_KEY = '_section_source_log'

  /** In-memory mirror of the persisted log. Null until first hydrated from /cart.js. */
  var state = null
  var hydrating = null
  /** True when `state` has local changes not yet confirmed written to Shopify. */
  var dirty = false

  // resolveCartUrl/fetchCart/liveQuantitiesFromCart were byte-identical between this file and its
  // sibling wonderful-experience-source-log.js — now shared via assets/wonderful-cart-log-shared.js
  // (should load first). fetchCart() in particular collapses concurrent callers (this module and its
  // sibling both fetching /cart.js around the same tick, e.g. checkout-experience.js's Promise.all)
  // into a single request instead of each firing its own. Falls back to a local, self-contained
  // implementation if the shared script didn't load (CDN hiccup, blocked request, script reordered)
  // rather than throwing — this module worked standalone before the shared file existed, and losing
  // the fetch-dedupe optimization is a much smaller problem than breaking recordDelta/hydrate outright.
  function resolveCartUrlFallback(path) {
    var root =
      typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
        ? String(Shopify.routes.root).replace(/\/?$/, '/')
        : '/'
    return root + path
  }

  function resolveCartUrl(path) {
    return window.WonderfulCartLogShared
      ? window.WonderfulCartLogShared.resolveCartUrl(path)
      : resolveCartUrlFallback(path)
  }

  function fetchCart() {
    if (window.WonderfulCartLogShared) return window.WonderfulCartLogShared.fetchCart()
    return fetch(resolveCartUrlFallback('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('GET /cart.js failed: ' + res.status)
      return res.json()
    })
  }

  function parseLog(cart) {
    var raw = cart && cart.attributes && cart.attributes[ATTR_KEY]
    if (!raw) return {}
    try {
      var parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (e) {
      return {}
    }
  }

  /**
   * Fire-and-forget write-back for stale entries pruned during hydrate (not tied to
   * `dirty`/markFlushed). Only ever POSTs this module's own attribute key.
   *
   * KNOWN LIMITATION: this races the sibling log's own writes (persistPrunedLog here and in
   * wonderful-experience-source-log.js, plus each module's own recordDelta-triggered flush) — if two
   * POSTs to /cart/update.js land out of send order, whichever completes last on the server wins for
   * whatever key(s) it carries. An earlier attempt to close this by having each prune-write also
   * carry a snapshot of the sibling's current in-memory attribute made this *worse*, not better: the
   * snapshot could itself be staler than a legitimate write already in flight from the sibling,
   * silently reverting it. A real fix needs either a confirmed atomic per-key merge on Shopify's end
   * (in which case there's no race to fix here at all) or genuine request sequencing — bigger in
   * scope than a client-side patch merge, and not worth guessing at without being able to verify
   * against the live cart API. Left as-is deliberately; do not "fix" this with a sibling-state merge.
   */
  function persistPrunedLog() {
    fetch(resolveCartUrl('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: buildAttributePatch() }),
    }).catch(function () {})
  }

  /**
   * Resolves once `state` reflects the persisted cart attribute (fetched at
   * most once). Also reconciles against live quantities right away — not just
   * at checkout — and prunes+persists immediately if anything was stale.
   */
  function hydrate() {
    if (state) return Promise.resolve(state)
    if (hydrating) return hydrating
    hydrating = fetchCart()
      .then(function (cart) {
        var parsed = parseLog(cart)
        var live = liveQuantitiesFromCart(cart)
        var result = reconcileAgainstLive(parsed, live)
        state = result.reconciled
        var changed = JSON.stringify(parsed) !== JSON.stringify(state)
        if (changed) persistPrunedLog()
        return state
      })
      .catch(function () {
        state = {}
        return state
      })
      .finally(function () {
        hydrating = null
      })
    return hydrating
  }

  /**
   * Records N unit adds (delta > 0, pushes `sectionId` N times) or removals
   * (delta < 0, pops N entries off the end — LIFO, regardless of `sectionId`).
   * `label` (e.g. product/flavor name) is accepted for parity with every call site (which all pass
   * it) but currently unused here — kept in the signature rather than trimmed from every caller.
   */
  function recordDelta(variantId, delta, sectionId, label) {
    var vid = String(variantId || '')
    var n = Math.trunc(Number(delta) || 0)
    if (!vid || n === 0) return Promise.resolve()
    // "CR_" prefix — see window.__WONDERFUL_CLASSIC_REQUESTED__ in sections/wonderful-cart-order-hero.liquid.
    // Page-lifetime-only signal that the classic-experience link was clicked earlier this page load; never
    // persisted, so a refresh/new page naturally stops prefixing. Only applies to adds (n > 0) — a removal
    // just pops whatever entries are already logged, regardless of what they're prefixed with. Computed
    // synchronously here (not inside the .then below) so it reflects the flag's state at call time, not
    // whatever it happens to be once hydrate() resolves.
    if (n > 0 && sectionId && window.__WONDERFUL_CLASSIC_REQUESTED__) sectionId = 'CR_' + sectionId
    return hydrate().then(function () {
      var arr = (state[vid] || []).slice()
      if (n > 0) {
        // Pushed even when sectionId is blank ("unknown section") — arr's length must stay in exact
        // 1:1 correspondence with physical units in add order, because removal below pops off the
        // end (LIFO) purely by position. Skipping the push for blank entries was tried and reverted:
        // it desynced arr's length from live qty, so a later removal could pop a legitimately-
        // attributed entry instead of the untracked unit that was actually removed, whenever a
        // tracked and an untracked add were interleaved. Blank entries are excluded from the
        // majority-vote tally by reconcileAgainstLive's `tally()` (`if (!sectionId) return`), so they
        // never influence checkout_section — the tradeoff is that reconcileAgainstLive's `untracked`
        // figure only ever reflects units that bypassed recordDelta entirely (native /cart page,
        // third-party apps), not blank-sectionId calls through this function; that's a debug-logging
        // undercount, not a correctness issue, and is the safer side to be wrong on.
        for (var i = 0; i < n; i++) arr.push(sectionId)
      } else {
        arr = arr.slice(0, Math.max(0, arr.length + n))
      }
      if (arr.length > 0) state[vid] = arr
      else delete state[vid]
      dirty = true
    })
  }

  /**
   * Reconciles a log against live per-variant quantities: trims arrays longer
   * than live qty (untracked decrease — exact, LIFO), leaves arrays shorter
   * than live qty as-is (untracked increase — those units don't vote).
   *
   * Unlike the binary classic/chat tally in wonderful-experience-source-log.js,
   * section ids are an open set, so this tallies counts per distinct section id.
   */
  function reconcileAgainstLive(logState, liveQuantitiesByVariant) {
    var totalsBySection = {}
    var untrackedTotal = 0
    var reconciled = {}
    var perVariant = {}
    var seenVariants = {}

    // Tallies by canonical section id — a "CR_"-prefixed entry (see recordDelta below) and its
    // unprefixed counterpart are the same underlying section for majority-vote purposes; the "CR_"
    // prefix answers a different question (was classic requested first?) than checkout_section does
    // (which section). The raw, still-prefixed strings stay intact in `reconciled`/`perVariant` below —
    // only the vote tally itself is normalized.
    function tally(sectionId) {
      if (!sectionId) return
      var canonical = sectionId.indexOf('CR_') === 0 ? sectionId.slice(3) : sectionId
      totalsBySection[canonical] = (totalsBySection[canonical] || 0) + 1
    }

    Object.keys(logState || {}).forEach(function (vid) {
      seenVariants[vid] = true
      var arr = (logState[vid] || []).slice()
      var live = liveQuantitiesByVariant[vid] || 0
      var trimmedStaleEntries = 0
      if (arr.length > live) {
        trimmedStaleEntries = arr.length - live
        arr = arr.slice(0, live)
      }
      var untracked = Math.max(0, live - arr.length)
      if (arr.length > 0) reconciled[vid] = arr
      arr.forEach(tally)
      untrackedTotal += untracked
      perVariant[vid] = {
        live: live,
        sections: arr.slice(),
        untracked: untracked,
        trimmedStaleEntries: trimmedStaleEntries,
      }
    })

    // Variants live in the cart with no log entry at all (never tracked, not just under-tracked).
    Object.keys(liveQuantitiesByVariant || {}).forEach(function (vid) {
      if (seenVariants[vid]) return
      var live = liveQuantitiesByVariant[vid] || 0
      if (live <= 0) return
      untrackedTotal += live
      perVariant[vid] = { live: live, sections: [], untracked: live, trimmedStaleEntries: 0 }
    })

    return {
      totalsBySection: totalsBySection,
      untrackedTotal: untrackedTotal,
      perVariant: perVariant,
      reconciled: reconciled,
    }
  }

  /** Highest-count section id, or null on a tie (including a 0-0 tie when nothing is tracked) — mirrors checkout_experience's tie/empty handling. */
  function majoritySection(totalsBySection) {
    var best = null
    var bestCount = 0
    var tied = false
    Object.keys(totalsBySection).forEach(function (sectionId) {
      var count = totalsBySection[sectionId]
      if (count > bestCount) {
        best = sectionId
        bestCount = count
        tied = false
      } else if (count === bestCount) {
        tied = true
      }
    })
    if (tied || !best) return null
    return best
  }

  function liveQuantitiesFromCart(cart) {
    if (window.WonderfulCartLogShared) return window.WonderfulCartLogShared.liveQuantitiesFromCart(cart)
    var map = {}
    ;(cart.items || []).forEach(function (line) {
      var vid = String(line.variant_id)
      map[vid] = (map[vid] || 0) + (line.quantity || 0)
    })
    return map
  }

  /** `patch[ATTR_KEY] = JSON.stringify(state)` shape used by every attribute-patch getter below. */
  function buildAttributePatch() {
    var patch = {}
    patch[ATTR_KEY] = JSON.stringify(state)
    return patch
  }

  /** Async patch for a `/cart/update.js` `attributes` payload, or null if nothing pending. */
  function getPendingAttributesPatch() {
    return hydrate().then(function () {
      if (!dirty) return null
      return buildAttributePatch()
    })
  }

  /** Best-effort sync variant for keepalive/beacon-style writes that can't await hydration. */
  function getPendingAttributesPatchSync() {
    if (!state || !dirty) return null
    return buildAttributePatch()
  }

  function markFlushed() {
    dirty = false
  }

  /**
   * Wipes the entire log unconditionally — for full-cart-clear actions, where
   * every variant's quantity hits 0 regardless of what the log currently
   * tracks.
   */
  function clearAll() {
    state = {}
    dirty = true
  }

  /**
   * Reconciles the log against a fresh live cart read, computes the
   * quantity-weighted majority, and returns the exact `attributes` patch
   * (log + `checkout_section`) to send in a single `/cart/update.js` call.
   */
  function computeCheckoutAttributes() {
    return hydrate()
      .then(function () {
        return fetchCart()
      })
      .then(function (cart) {
        var live = liveQuantitiesFromCart(cart)
        var result = reconcileAgainstLive(state, live)
        state = result.reconciled
        dirty = false
        var majority = majoritySection(result.totalsBySection)
        var attributes = buildAttributePatch()
        attributes.checkout_section = majority || ''
        return { attributes: attributes, majority: majority }
      })
      .catch(function (e) {
        console.warn('[Wonderful Section Log] computeCheckoutAttributes failed', e)
        return { attributes: { checkout_section: '' }, majority: null }
      })
  }

  window.WonderfulSectionLog = {
    recordDelta: recordDelta,
    getPendingAttributesPatch: getPendingAttributesPatch,
    getPendingAttributesPatchSync: getPendingAttributesPatchSync,
    markFlushed: markFlushed,
    clearAll: clearAll,
    computeCheckoutAttributes: computeCheckoutAttributes,
  }
})()
